import React, { useEffect, useMemo, useState } from 'react';
import { pinyin } from 'pinyin-pro';
import { apiFetch } from '../api';
import { useAuth } from '../auth';
import type { RecentTool, Tool } from '../types';

function toolHref(toolId: string) {
  const map: Record<string, string> = {
    tarot: '/tools/tarot',
    constellation: '/tools/constellation',
    liuyao: '/tools/liuyao',
    meihua: '/tools/meihua',
    qimen: '/tools/qimen',
    ziwei: '/tools/ziwei',
    bazi: '/tools/bazi',
    wheel: '/tools/wheel',
    nickname: '/tools/nickname',
    mbti: '/tools/mbti',
    zodiac: '/tools/zodiac',
    catchthecat: '/tools/catchthecat',
    astrology: '/tools/astrology',
    liferestart: '/tools/liferestart',
    game2048: '/tools/2048',
    game2048lite: '/tools/2048-lite'
  };
  return map[toolId] || '/';
}

export function HomePage() {
  const { user } = useAuth();
  const [tools, setTools] = useState<Tool[]>([]);
  const [recent, setRecent] = useState<RecentTool[]>([]);
  const [scope, setScope] = useState<'local' | 'ai'>('local');
  const [tab, setTab] = useState<'popular' | 'recent' | 'all'>('popular');
  const [allSort, setAllSort] = useState<'hot' | 'az'>('hot');

  useEffect(() => {
    apiFetch<{ ok: boolean; tools: Tool[] }>('/api/tools', { method: 'GET' })
      .then((r) => setTools(r.tools || []))
      .catch(() => setTools([]));
  }, []);

  useEffect(() => {
    if (!user) {
      setRecent([]);
      return;
    }
    apiFetch<{ ok: boolean; recent: RecentTool[] }>('/api/user/recent-tools', { method: 'GET' })
      .then((r) => setRecent(r.recent || []))
      .catch(() => setRecent([]));
  }, [user]);

  useEffect(() => {
    if (!user && tab === 'recent') setTab('popular');
  }, [user, tab]);

  const scopedTools = useMemo(() => {
    const wantAi = scope === 'ai';
    return tools
      .filter((t) => (!!t.requiresAi) === wantAi)
      .slice()
      .sort((a, b) => a.sort - b.sort);
  }, [tools, scope]);

  const scopedRecent = useMemo(() => {
    const wantAi = scope === 'ai';
    return recent.filter((t) => (!!t.requiresAi) === wantAi);
  }, [recent, scope]);

  const nameCollator = useMemo(() => {
    return new Intl.Collator(['zh-Hans-CN-u-co-pinyin', 'zh-CN', 'en'], { sensitivity: 'base', numeric: true });
  }, []);

  const popularTools = useMemo(() => {
    return scopedTools
      .slice()
      .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0) || a.sort - b.sort)
      .slice(0, 9);
  }, [scopedTools]);

  const allToolsHot = useMemo(() => {
    return scopedTools
      .slice()
      .sort(
        (a, b) =>
          (b.usageCount || 0) - (a.usageCount || 0) ||
          nameCollator.compare(String(a.name || ''), String(b.name || '')) ||
          a.id.localeCompare(b.id)
      );
  }, [scopedTools, nameCollator]);

  const allToolsAz = useMemo(() => {
    return scopedTools
      .slice()
      .sort((a, b) => nameCollator.compare(String(a.name || ''), String(b.name || '')) || a.id.localeCompare(b.id));
  }, [scopedTools, nameCollator]);

  const groupedAz = useMemo(() => {
    const getInitial = (t: Tool) => {
      const n = String(t.name || '').trim();
      const m = n.match(/^[A-Za-z]/);
      if (m) return m[0].toUpperCase();
      try {
        const first = pinyin(n, { pattern: 'first', toneType: 'none' });
        const m2 = String(first || '').match(/[A-Za-z]/);
        return m2 ? m2[0].toUpperCase() : '#';
      } catch {
        return '#';
      }
    };
    const buckets: Record<string, Tool[]> = {};
    for (const t of allToolsAz) {
      const k = getInitial(t);
      if (!buckets[k]) buckets[k] = [];
      buckets[k].push(t);
    }
    const letters = Object.keys(buckets)
      .filter((k) => k !== '#')
      .sort((a, b) => a.localeCompare(b, 'en'));
    const ordered = letters.length ? letters : [];
    if (buckets['#']?.length) ordered.push('#');
    return { letters: ordered, buckets };
  }, [allToolsAz]);

  const title = scope === 'local' ? '云境本地趣用' : '云智联动趣用';
  const subtitle =
    scope === 'local'
      ? '无需联云智服，轻触即可启玩'
      : '需联云智服（可于“我的小馆”设定），以供生成/解读';

  const listTitle = tab === 'popular' ? '小馆荐玩' : tab === 'recent' ? '近来雅玩' : '馆内全席';
  const listSubtitle =
    tab === 'popular'
      ? '甄选馆内趣用，仅呈九席热门'
      : tab === 'recent'
        ? '入馆后，小馆为你记下近来入席'
        : allSort === 'hot'
          ? '默认按热度陈列（热的在前）'
          : '按名序 A-Z 陈列，并依首字母分架';

  const renderTool = (t: Tool | RecentTool) => {
    return (
      <a key={t.id} href={toolHref(t.id)} className="tool">
        <div className="tool-top">
          <div className="tool-name">{t.name}</div>
          <span className="badge">馆内雅用：{t.usageCount || 0} 次</span>
        </div>
        {'lastUsedAt' in t ? <div className="muted">近来：{new Date((t as RecentTool).lastUsedAt).toLocaleString()}</div> : null}
        {'userUseCount' in t ? <div className="muted">你已入席：{(t as RecentTool).userUseCount} 次</div> : <div className="muted">轻触入席</div>}
      </a>
    );
  };

  return (
    <div className="container">
      <div className="hero">
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>

      <div className="row" style={{ marginBottom: 10 }}>
        <button className={`pill ${scope === 'local' ? 'active' : ''}`} onClick={() => setScope('local')}>
          云境本地趣用
        </button>
        <button className={`pill ${scope === 'ai' ? 'active' : ''}`} onClick={() => setScope('ai')}>
          云智联动趣用
        </button>
      </div>

      <div className="row" style={{ marginBottom: 12 }}>
        <button className={`pill ${tab === 'popular' ? 'active' : ''}`} onClick={() => setTab('popular')}>
          小馆荐玩
        </button>
        {user ? (
          <button className={`pill ${tab === 'recent' ? 'active' : ''}`} onClick={() => setTab('recent')}>
            近来雅玩
          </button>
        ) : (
          <span className="pill">入馆后方可见近来雅玩</span>
        )}
        <button className={`pill ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>
          馆内全席
        </button>
      </div>

      <div className="card">
        <div className="card-inner">
          <div className="section-title">
            <div>
              <h2>{listTitle}</h2>
              <span className="muted">{listSubtitle}</span>
            </div>
            {tab === 'all' ? (
              <div className="row" style={{ justifyContent: 'flex-end' }}>
                <button className={`pill ${allSort === 'hot' ? 'active' : ''}`} onClick={() => setAllSort('hot')}>
                  热度
                </button>
                <button className={`pill ${allSort === 'az' ? 'active' : ''}`} onClick={() => setAllSort('az')}>
                  名序 A-Z
                </button>
              </div>
            ) : null}
          </div>

          {tab === 'all' ? (
            allSort === 'az' ? (
              groupedAz.letters.length ? (
                <>
                  <div className="row" style={{ marginBottom: 12 }}>
                    {groupedAz.letters
                      .filter((l) => l !== '#')
                      .map((l) => (
                        <a key={l} href={`#tools-letter-${l}`} className="pill">
                          {l}
                        </a>
                      ))}
                  </div>
                  {groupedAz.letters.map((l, idx) => (
                    <div key={l} style={{ marginTop: idx === 0 ? 0 : 14 }}>
                      <div id={`tools-letter-${l}`} className="muted" style={{ marginBottom: 8 }}>
                        {l}
                      </div>
                      <div className="tool-grid">{(groupedAz.buckets[l] || []).map(renderTool)}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="muted">暂无席位</div>
              )
            ) : allToolsHot.length ? (
              <div className="tool-grid">{allToolsHot.map(renderTool)}</div>
            ) : (
              <div className="muted">暂无席位</div>
            )
          ) : (
            <div className="tool-grid">{(tab === 'recent' ? scopedRecent : popularTools).map(renderTool)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
